import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Home, Download, Eye, Sparkles, Loader2, AlertCircle, CheckCircle2, Package } from "lucide-react";
import { toast } from "sonner";

function pickLatest(documents, templateKey) {
  return (documents || [])
    .filter((d) => d.template_key === templateKey)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
}

const ALL_OTP_TEMPLATES = [
  { key: "otp_main",                      label: "Offer to Purchase (Main)",                         blurb: "Master OTP with conditional clauses driven by the flags below." },
  { key: "otp_annexure_a_defects",        label: "Annexure A — Property Condition Disclosure",       blurb: "Defects declaration completed by the Seller." },
  { key: "otp_annexure_b_fica_seller",    label: "Annexure B — Seller FICA Checklist",               blurb: "FICA document checklist for the Seller (natural/juristic/deceased estate)." },
  { key: "otp_annexure_c_fica_purchaser", label: "Annexure C — Purchaser FICA Checklist",            blurb: "FICA document checklist for the Purchaser." },
  { key: "otp_annexure_d_agent_mandate",  label: "Annexure D — Estate Agent Commission Mandate",     blurb: "Commission mandate — generated only when an estate agent is involved." },
];

/**
 * OTP admin panel — renders only for conveyancing / OTP matters.
 * Header carries the conditional-clause flag toggles (property type, VAT/duty,
 * bond/cash, agent on/off, HOA, deceased estate). Toggling a flag PATCHes the
 * matter's `otp_flags` so the next generation picks up the new clause logic.
 */
export default function OtpDocumentActions({ matter, documents, onChanged }) {
  const mtype = matter?.matter_type || matter?.service_type;
  if (mtype !== "conveyancing" && mtype !== "otp") return null;

  const flags = matter?.entities?.otp_flags || {};
  const agentInvolved = !!flags.agent_involved;
  const templates = ALL_OTP_TEMPLATES.filter((t) =>
    t.key !== "otp_annexure_d_agent_mandate" || agentInvolved
  );
  const anyDraft = templates.some((t) => pickLatest(documents, t.key));

  const setFlag = async (key, value) => {
    try {
      await api.patch(`/admin/matters/${matter.id}/entities`, {
        otp_flags: { ...flags, [key]: value },
      });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadBundle = () => {
    api.get(`/admin/matters/${matter.id}/otp-bundle.zip`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/zip" });
        const ref  = (matter.id || "").slice(0, 8).toUpperCase();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `EH_Inc_OTP_Bundle_${ref}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        toast.success("OTP bundle downloaded");
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div className="bg-white border border-zinc-200 mb-5" data-testid="otp-doc-actions">
      <div className="px-5 py-3 bg-zinc-900 text-white flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-300">EH Inc · OTP Workspace</div>
            <div className="text-sm font-medium">
              Offer to Purchase &amp; Annexures
            </div>
          </div>
        </div>
        {anyDraft && (
          <button
            type="button"
            onClick={downloadBundle}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-medium bg-white text-zinc-900 hover:bg-zinc-100"
            data-testid="download-otp-bundle-btn"
          >
            <Package className="h-3.5 w-3.5" />
            Download OTP Bundle (ZIP)
          </button>
        )}
      </div>

      {/* CONDITIONAL CLAUSE FLAGS */}
      <div className="px-5 py-4 bg-zinc-50 border-b border-zinc-200 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs" data-testid="otp-flags-panel">
        <FlagRadio label="Property type" value={flags.property_type || "freehold"}
          onChange={(v) => setFlag("property_type", v)}
          testid="otp-flag-property-type"
          options={[
            { value: "freehold",        label: "Freehold" },
            { value: "sectional_title", label: "Sectional Title" },
            { value: "agricultural",    label: "Agricultural" },
          ]} />
        <FlagRadio label="Tax" value={flags.vat_applies ? "vat" : "duty"}
          onChange={(v) => setFlag("vat_applies", v === "vat")}
          testid="otp-flag-tax"
          options={[
            { value: "duty", label: "Transfer Duty" },
            { value: "vat",  label: "VAT" },
          ]} />
        <FlagRadio label="Financing" value={flags.bond_required === false ? "cash" : "bond"}
          onChange={(v) => setFlag("bond_required", v === "bond")}
          testid="otp-flag-financing"
          options={[
            { value: "bond", label: "Bond" },
            { value: "cash", label: "Cash" },
          ]} />
        <FlagToggle label="Estate Agent involved" value={!!flags.agent_involved}
          onChange={(v) => setFlag("agent_involved", v)} testid="otp-flag-agent" />
        <FlagToggle label="Has HOA" value={!!flags.has_hoa}
          onChange={(v) => setFlag("has_hoa", v)} testid="otp-flag-hoa" />
        <FlagToggle label="72-hour clause" value={!!flags.sale_of_existing}
          onChange={(v) => setFlag("sale_of_existing", v)} testid="otp-flag-72hr" />
      </div>

      <ul className="divide-y divide-zinc-200">
        {templates.map((t) => (
          <OtpRow key={t.key} label={t.label} blurb={t.blurb} templateKey={t.key}
                  matter={matter} doc={pickLatest(documents, t.key)} onChanged={onChanged} />
        ))}
      </ul>
    </div>
  );
}


function FlagRadio({ label, value, options, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="flex flex-wrap gap-1" data-testid={testid}>
        {options.map((opt) => (
          <button key={opt.value} type="button"
            onClick={() => onChange(opt.value)}
            className={`px-2 py-1 text-[11px] border ${value === opt.value ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"}`}>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function FlagToggle({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <button type="button" onClick={() => onChange(!value)} data-testid={testid}
        className={`w-full px-2 py-1 text-[11px] border ${value ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"}`}>
        {value ? "YES" : "NO"}
      </button>
    </div>
  );
}


function OtpRow({ label, blurb, templateKey, matter, doc, onChanged }) {
  const [busy, setBusy]       = useState(false);
  const [missing, setMissing] = useState(null);

  const generate = async () => {
    setBusy(true);
    setMissing(null);
    try {
      const { data } = await api.post(
        `/admin/matters/${matter.id}/documents/generate-structured`,
        { template_key: templateKey },
      );
      toast.success(`${label} generated`, { description: data?.audit_event?.summary || "Draft ready for review." });
      onChanged?.();
    } catch (e) {
      const body   = e?.response?.data;
      if (e?.response?.status === 422 && body?.blocked) {
        setMissing(body.missing || []);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const fetchPdf = (cb) => {
    if (!doc) return;
    api.get(`/admin/documents/${doc.id}/pdf`, { responseType: "blob" })
      .then((res) => cb(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))))
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <li className="p-5" data-testid={`otp-row-${templateKey}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="font-medium text-sm text-zinc-900">{label}</div>
          <div className="text-xs text-zinc-500 mt-1">{blurb}</div>
          {doc && (
            <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="h-3 w-3" /> Draft generated
              </span>
              <span className="text-zinc-400">·</span>
              <span>{new Date(doc.created_at).toLocaleString()}</span>
              <span className="text-zinc-400">·</span>
              <span className="uppercase tracking-wider text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5">
                {(doc.status || "").replace(/_/g, " ")}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {doc ? (
            <>
              <Button variant="outline" size="sm" className="rounded-none"
                onClick={() => fetchPdf((u) => { window.open(u, "_blank", "noopener,noreferrer"); setTimeout(() => URL.revokeObjectURL(u), 60_000); })}
                data-testid={`preview-${templateKey}-btn`}>
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Preview
              </Button>
              <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800"
                onClick={() => fetchPdf((u) => { const a = document.createElement("a"); a.href = u; a.download = `${(doc.template_name || label).replace(/\s+/g, "_")}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u); })}
                data-testid={`download-${templateKey}-btn`}>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download
              </Button>
              <Link to={`/admin/matters/${matter.id}/documents/${doc.id}`}
                className="text-xs text-zinc-500 hover:text-zinc-900 px-2"
                data-testid={`open-${templateKey}-workspace`}>
                Workspace →
              </Link>
            </>
          ) : (
            <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-800" onClick={generate} disabled={busy}
              data-testid={`generate-${templateKey}-btn`}>
              {busy
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</>
                : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate</>}
            </Button>
          )}
        </div>
      </div>

      {missing && missing.length > 0 && (
        <div className="mt-3 border border-amber-300 bg-amber-50 p-3" data-testid={`otp-missing-${templateKey}`}>
          <div className="text-xs font-medium text-amber-900 flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            Complete required fields to generate {label}
          </div>
          <ul className="text-[12px] text-amber-800 list-disc pl-5 mt-1.5 space-y-0.5">
            {missing.map((f) => <li key={f}>{f}</li>)}
          </ul>
        </div>
      )}
    </li>
  );
}
